import React from "react";
import { motion } from "framer-motion";
import { ActiveInspection } from "../types";

interface InspectionCardHeaderProps {
  inspection: ActiveInspection;
  isDarkMode: boolean;
  updatingStatus: boolean;
  onOpenDetail: () => void;
  onDownloadReport: () => void;
  onConfirmClose: () => void;
}

export const InspectionCardHeader: React.FC<InspectionCardHeaderProps> = ({
  inspection,
  isDarkMode,
  updatingStatus,
  onOpenDetail,
  onDownloadReport,
  onConfirmClose,
}) => {
  return (
    <div
      className={`py-1.5 px-3 ${
        isDarkMode
          ? "bg-gradient-to-r from-phoenix-700 to-phoenix-900"
          : "bg-gradient-to-r from-phoenix-500 to-phoenix-700"
      }`}
    >
      <div className="flex justify-between items-center gap-3">
        {/* Izquierda: Número del vehículo + Mechanic/Driver */}
        <div className="flex items-center gap-2">
          {/* Número del vehículo */}
          <div className="bg-white rounded-lg px-2.5 py-0.5 shadow-lg">
            <span className="text-2xl font-black text-phoenixBrand tracking-tight">
              {inspection.vehicle_number}
            </span>
          </div>
          {/* Mechanic arriba, Driver abajo */}
          <div className="flex flex-col">
            {inspection.mechanic_name && (
              <div className="text-[10px] text-white font-medium leading-tight">
                Mechanic: <strong>{inspection.mechanic_name}</strong>
              </div>
            )}
            {inspection.driver_name && (
              <div className="text-[10px] text-white font-medium leading-tight">
                Driver: <strong>{inspection.driver_name}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Derecha: Botones */}
        <div className="flex gap-2">
          <button
            onClick={onOpenDetail}
            title="Mark checklist items"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/20 hover:bg-white/30 text-white transition-all hover:shadow-lg flex items-center justify-center gap-1.5 backdrop-blur-sm"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
            <span className="hidden lg:inline">Mark</span>
          </button>
          <button
            onClick={onDownloadReport}
            title="Download PDF report"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-700 hover:bg-gray-600 text-white transition-all hover:shadow-lg flex items-center gap-1.5"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="hidden lg:inline">Report</span>
          </button>
          <button
            onClick={onConfirmClose}
            disabled={updatingStatus}
            title={
              inspection.status === "open"
                ? "Close inspection"
                : "Reopen inspection"
            }
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:shadow-lg flex items-center gap-1.5 ${
              inspection.status === "open"
                ? "bg-orange-600 hover:bg-orange-700 text-white"
                : "bg-amber-600 hover:bg-amber-700 text-white"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  inspection.status === "open"
                    ? "M5 13l4 4L19 7"
                    : "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                }
              />
            </svg>
            <span className="hidden lg:inline">
              {inspection.status === "open" ? "Close" : "Reopen"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
